'use client';

import { isPlainObject } from 'es-toolkit/predicate';
import { CheckCircle, XCircle } from 'lucide-react';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { CuteSpinner } from '@/components/common/CuteSpinner';

import { buildToolArgumentSummary } from './argument-registry';
import { AnalyzeTrendsResultCard } from './result-cards/AnalyzeTrendsResultCard';
import { ArchiveTweetsResult } from './result-cards/ArchiveTweetsResult';
import { CompareEngagementResultCard } from './result-cards/CompareEngagementResultCard';
import {
  EmptyResult,
  GenericArrayResult,
  GenericObjectResult,
  SimpleResult,
  ToolError,
} from './result-cards/GenericObjectResult';
import { SearchTimelineResult } from './result-cards/SearchTimelineResult';
import { TweetResultCard } from './result-cards/TweetResultCard';
import { UserProfileResultCard } from './result-cards/UserProfileResultCard';
import {
  isAnalyzeTrendsToolResult,
  isArchiveTweetResult,
  isCompareEngagementToolResult,
  isSearchTimelineToolResult,
  isTweetToolResult,
  isUserProfileToolResult,
} from './type-guards';
import type { ToolExecutionState } from './types';
import { buildToolRenderState, extractQueryArg as extractQueryArgument, pluralize } from './utils';

export interface ToolExecutionProperties {
  args?: Record<string, unknown>;
  result?: Record<string, unknown> | string | number | null | unknown[];
  state: ToolExecutionState;
  toolName: string;
}

type ToolRendererContext = {
  args: Record<string, unknown>;
};

type ToolRenderer = (payload: unknown, context: ToolRendererContext) => ReactElement | null;

const resolveToolDescription = (toolName: string): string => {
  switch (toolName) {
    case 'searchTimeline': {
      return 'Searching your timeline';
    }
    case 'getTweet': {
      return 'Fetching tweet details';
    }
    case 'getUserProfile': {
      return 'Loading user profile';
    }
    case 'getConversation': {
      return 'Loading conversation thread';
    }
    case 'analyzeTrends': {
      return 'Analyzing trending topics';
    }
    case 'compareEngagement': {
      return 'Comparing engagement metrics';
    }
    case 'getUserTweets': {
      return 'Fetching user tweets';
    }
    case 'getImages': {
      return 'Extracting images from tweets';
    }
    case 'getImportStatus': {
      return 'Checking import progress';
    }
    case 'getProfileHighlights': {
      return 'Summarizing profile insights';
    }
    case 'getArchiveData': {
      return 'Fetching archive data';
    }
    case 'setDailyDigestPreference': {
      return 'Updating daily digest settings';
    }
    case 'setPulsePreference': {
      return 'Updating Pulse settings';
    }
    default: {
      return `Calling ${toolName}`;
    }
  }
};

const getToolRenderer = (toolName: string): ToolRenderer | null => {
  switch (toolName) {
    case 'searchTimeline': {
      return (payload, context) =>
        isSearchTimelineToolResult(payload) ? (
          <SearchTimelineResult result={payload} query={extractQueryArgument(context.args)} />
        ) : null;
    }
    case 'getTweet': {
      return (payload) => (isTweetToolResult(payload) ? <TweetResultCard result={payload} /> : null);
    }
    case 'getUserProfile': {
      return (payload) => (isUserProfileToolResult(payload) ? <UserProfileResultCard result={payload} /> : null);
    }
    case 'analyzeTrends': {
      return (payload) => (isAnalyzeTrendsToolResult(payload) ? <AnalyzeTrendsResultCard result={payload} /> : null);
    }
    case 'compareEngagement': {
      return (payload) =>
        isCompareEngagementToolResult(payload) ? <CompareEngagementResultCard result={payload} /> : null;
    }
    case 'getArchiveData': {
      return (payload) => (isArchiveTweetResult(payload) ? <ArchiveTweetsResult result={payload} /> : null);
    }
    default: {
      return null;
    }
  }
};

export function ToolExecution({ toolName, args, state, result }: ToolExecutionProperties) {
  const description = resolveToolDescription(toolName);
  const normalizedArguments = useMemo(() => args ?? {}, [args]);
  const argumentSnapshot = useMemo(() => buildToolArgumentSummary(toolName, args), [toolName, args]);

  const { status, errorMessage, hasResult, emptyStateMessage } = useMemo(
    () => buildToolRenderState(state, result),
    [result, state]
  );

  const renderedResult = useMemo<ReactElement | null>(() => {
    if (status === 'error' && errorMessage) {
      return <ToolError message={errorMessage} />;
    }

    if (!hasResult) {
      return null;
    }

    const renderer = getToolRenderer(toolName);
    const rendered = renderer ? renderer(result, { args: normalizedArguments }) : null;
    if (rendered) {
      return rendered;
    }

    return renderFallbackResult(result);
  }, [errorMessage, hasResult, normalizedArguments, result, status, toolName]);

  const isLoading = status === 'loading';
  const isSuccess = status === 'success';
  const isError = status === 'error';

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {isLoading && <CuteSpinner size={16} className="text-primary border-primary/30 border-t-primary" />}
        {isSuccess && <CheckCircle className="h-4 w-4 text-success" />}
        {isError && <XCircle className="h-4 w-4 text-destructive" />}
        <span className="font-medium text-foreground">{description}</span>
        {renderArgumentLabel('query', argumentSnapshot.query)}
        {renderArgumentLabel('username', argumentSnapshot.username, '@')}
        {renderArgumentLabel('tweetId', argumentSnapshot.tweetId, 'ID: ')}
        {renderArgumentLabel('dataset', argumentSnapshot.dataset)}
      </div>

      {renderedResult}

      {emptyStateMessage ? <EmptyResult message={emptyStateMessage} /> : null}
    </div>
  );
}

function renderFallbackResult(result: unknown): ReactElement {
  if (typeof result === 'string' || typeof result === 'number') {
    return <SimpleResult text={String(result)} />;
  }

  if (Array.isArray(result)) {
    return <GenericArrayResult items={result} />;
  }

  if (isPlainObject(result)) {
    return <GenericObjectResult data={result} />;
  }

  return <EmptyResult message={`No renderer available for ${typeof result} results.`} />;
}

function renderArgumentLabel(key: string, value: unknown, prefix = ''): ReactElement | null {
  if (typeof value === 'string' || typeof value === 'number') {
    const label = key === 'query' ? `“${String(value)}”` : `${prefix}${String(value)}`;
    return <span className="text-muted-foreground">{label}</span>;
  }
  if (Array.isArray(value)) {
    return <span className="text-muted-foreground">{pluralize(value.length, key)}</span>;
  }
  return null;
}
